import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { renderAsync } from "docx-preview";
import { api, apiErr } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { SignaturePad } from "@/components/SignaturePad";
import { Loader2, ShieldCheck, CheckCircle2, AlertTriangle, Eraser, PenLine } from "lucide-react";

// Public page (no login): client opens one-time link → reviews retainer → types name + draws signature → agrees.
export default function SignRetainer() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [session, setSession] = useState("");
  const [busy, setBusy] = useState(false);
  const [docReady, setDocReady] = useState(false);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [initials, setInitials] = useState("");
  const [hasInk, setHasInk] = useState(false);
  const [done, setDone] = useState(null);
  const docRef = useRef(null);
  const padRef = useRef(null);

  useEffect(() => {
    api.get(`/public/sign/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(apiErr(e)));
  }, [token]);

  useEffect(() => {
    if (!session) return;
    api.get(`/public/sign/${token}/document`, { headers: { "X-Sign-Session": session }, responseType: "arraybuffer" })
      .then(async (r) => {
        if (docRef.current) {
          docRef.current.innerHTML = "";
          await renderAsync(new Blob([r.data]), docRef.current, null, { inWrapper: true, ignoreLastRenderedPageBreak: true });
          markSignSpots(docRef.current);
        }
        setDocReady(true);
      })
      .catch(() => setError("Could not load the retainer. Please refresh the page and try again."));
  }, [session, token]);

  const verify = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { const r = await api.post(`/public/sign/${token}/verify`, {}); setSession(r.data.session); }
    catch (err) { setError(apiErr(err)); } finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post(`/public/sign/${token}/submit`, { typed_name: name, initials, agree, signature: padRef.current.toDataURL() }, { headers: { "X-Sign-Session": session } });
      setDone(r.data);
    } catch (err) { setError(apiErr(err)); } finally { setBusy(false); }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200"><div className="max-w-4xl mx-auto px-4 py-4"><Brand /></div></header>
      <main className="max-w-4xl mx-auto px-4 py-8">{children}</main>
    </div>
  );

  if (!info && !error) return shell(<div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>);
  if (done || info?.status === "Signed") return shell(
    <div data-testid="sign-success" className="bg-white rounded-2xl border border-slate-200 p-8 text-center space-y-3">
      <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
      <h1 className="font-head font-extrabold text-2xl text-slate-900">Retainer signed</h1>
      <p className="text-slate-600 text-sm">Thank you{name ? `, ${name}` : ""}. Your signed retainer has been securely saved with {info?.company || "Chahal Overseas Immigration"}. Our team will be in touch with next steps.</p>
    </div>
  );
  if (!info || info.status !== "Pending") return shell(
    <div data-testid="sign-unavailable" className="bg-white rounded-2xl border border-slate-200 p-8 text-center space-y-3">
      <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
      <h1 className="font-head font-bold text-xl text-slate-900">Signing link unavailable</h1>
      <p className="text-slate-600 text-sm">{error || (info?.status === "Expired" ? "This link has expired." : info?.status === "Locked" ? "This link has been locked." : "This link is no longer valid.")} Please contact Chahal Overseas Immigration for a new link.</p>
    </div>
  );

  if (!session) return shell(
    <form onSubmit={verify} className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md mx-auto space-y-4" data-testid="sign-verify-form">
      <ShieldCheck className="h-10 w-10 text-primary" />
      <div>
        <h1 className="font-head font-extrabold text-2xl text-slate-900">Hello{info.client_first_name ? ` ${info.client_first_name}` : ""},</h1>
        <p className="text-slate-600 text-sm mt-1">{info.company} has sent you a retainer agreement{info.service ? ` for ${info.service}` : ""} to review and sign.</p>
      </div>
      {error && <p data-testid="sign-error" className="text-sm text-rose-600">{error}</p>}
      <button data-testid="sign-verify-submit" disabled={busy} className="w-full bg-primary text-white py-3 rounded-xl font-semibold disabled:opacity-50">{busy ? "Opening…" : "Review retainer"}</button>
    </form>
  );

  return shell(
    <div className="space-y-6">
      <div>
        <h1 className="font-head font-extrabold text-2xl text-slate-900">Review your retainer agreement</h1>
        <p className="text-slate-600 text-sm mt-1">Please read the full agreement below. The places that need your action are highlighted in yellow.</p>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900" data-testid="sign-where-guide">
        <div className="font-semibold mb-1 inline-flex items-center gap-2"><PenLine className="h-4 w-4" /> Where you need to sign</div>
        <ol className="list-decimal ml-5 space-y-0.5">
          <li><b>Initial the Fees section</b> — the Fees, Deposit and Remaining Balance lines. Enter your initials below.</li>
          <li><b>Sign on the "Client/Client’s Representative" line</b> at the end of the agreement. Draw your signature below.</li>
        </ol>
        <p className="text-xs text-amber-800 mt-2">The RCIC (Amarvir Singh) will countersign after you. You will not need to sign the RCIC line.</p>
      </div>
      <div className="bg-slate-200 rounded-2xl p-2 max-h-[70vh] overflow-auto" data-testid="sign-document">
        {!docReady && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /></div>}
        <div ref={docRef} />
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4" data-testid="sign-form">
        <div>
          <label className="text-xs font-medium text-slate-500">Type your full legal name</label>
          <input data-testid="sign-typed-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500">1. Your initials — acknowledging the Fees, Deposit and Remaining Balance</label>
          <input data-testid="sign-initials" value={initials} maxLength={5} onChange={(e) => setInitials(e.target.value.replace(/[^a-zA-Z.]/g, "").toUpperCase())} placeholder="e.g. RS"
            className="mt-1 w-32 px-4 py-3 rounded-xl border border-slate-200 tracking-widest font-semibold" />
        </div>
        <div>
          <div className="flex items-center justify-between"><label className="text-xs font-medium text-slate-500">2. Draw your signature — for the "Client/Client’s Representative" line</label>
            <button type="button" data-testid="sign-clear" onClick={() => padRef.current?.clear()} className="text-xs text-slate-500 inline-flex items-center gap-1"><Eraser className="h-3.5 w-3.5" /> Clear</button></div>
          <SignaturePad ref={padRef} onInk={setHasInk} testid="sign-canvas" className="mt-1" />
        </div>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input data-testid="sign-agree" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" />
          <span>I have read and understood this retainer agreement, and I agree to its terms. I agree that my electronic signature is the legal equivalent of my handwritten signature.</span>
        </label>
        {error && <p data-testid="sign-error" className="text-sm text-rose-600">{error}</p>}
        <button data-testid="sign-submit" onClick={submit} disabled={busy || !docReady || !agree || !hasInk || name.trim().length < 2 || initials.replace(".", "").length < 1} className="w-full bg-primary text-white py-3 rounded-xl font-semibold disabled:opacity-50">{busy ? "Signing…" : "Sign retainer"}</button>
      </div>
    </div>
  );
}

// Highlight the fee block (initials) and the client signature line inside the rendered retainer.
function markSignSpots(root) {
  const paras = Array.from(root.querySelectorAll("p"));
  const badge = (text) => {
    const b = document.createElement("div");
    b.textContent = text;
    b.setAttribute("style", "display:inline-block;margin:4px 0;padding:2px 8px;border-radius:6px;background:#fde047;color:#713f12;font:600 11px Arial,sans-serif");
    return b;
  };
  const fees = paras.filter((p) => /^\s*(FEES:|Deposit:|Remaining Balance:)/i.test(p.textContent || ""));
  fees.forEach((p) => { p.style.background = "#fef9c3"; });
  const lastFee = fees[fees.length - 1];
  if (lastFee) lastFee.after(badge("✍ Initial here — enter your initials in step 1 below"));
  const clientLine = paras.filter((p) => /^\s*Client\/Client/i.test(p.textContent || "")).pop();
  if (clientLine) {
    clientLine.style.background = "#fef9c3";
    let line = clientLine.previousElementSibling;
    while (line && !(line.textContent || "").trim()) line = line.previousElementSibling;
    (line && /^_+$/.test((line.textContent || "").trim()) ? line : clientLine).before(badge("✍ Sign here — draw your signature in step 2 below"));
  }
  const rcic = paras.filter((p) => /^\s*RCIC/i.test(p.textContent || "")).pop();
  if (rcic) rcic.after(badge("Signed by the RCIC after you — no action needed"));
}
